import { TRPCClientError } from "@trpc/client";

/**
 * Structured cause extracted from a REQUIREMENTS_BLOCKED TRPCError.
 * The backend embeds this on `TRPCError.cause`, which the API's
 * errorFormatter lifts to `shape.data.cause` for BAD_REQUEST errors so it
 * reaches the client.
 */
export interface RequirementsBlockedCause {
  code: "REQUIREMENTS_BLOCKED";
  failed: Array<{
    id: string;
    reasonCode: string;
    displayLabel: string;
    deepLink?: string;
  }>;
}

/**
 * Parse a tRPC error to determine if it is a REQUIREMENTS_BLOCKED error.
 * Returns null otherwise. Used by every provider-side `setDecision` caller
 * to render the inline blocker uniformly.
 */
export function parseRequirementsBlockedError(
  error: unknown,
): RequirementsBlockedCause | null {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shapeCause = (error as any)?.shape?.data?.cause as
    | Partial<RequirementsBlockedCause>
    | undefined;

  if (
    shapeCause?.code === "REQUIREMENTS_BLOCKED" &&
    Array.isArray(shapeCause.failed)
  ) {
    return shapeCause as RequirementsBlockedCause;
  }

  // Fallback: detect by message string when cause isn't forwarded yet
  // (defensive — shouldn't trigger now that errorFormatter lifts cause).
  if (
    error.data?.code === "BAD_REQUEST" &&
    error.message === "Application requirements not met"
  ) {
    return { code: "REQUIREMENTS_BLOCKED", failed: [] };
  }

  return null;
}
